import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, CardContent, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { MprcCard, MprcCardActions, MprcCardHeaderColor, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator } from "components";
import { ButtonCancel, ButtonConfirm } from "components/buttons";
import { GetCurrentAgreementQueryResponse, StandardAccountAgreeResponse, StandardAccountAgreeStatus } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

type AccountAgreementProps = {
    securityStamp: string;
    reason: "NotAssigned" | "Update";
    onComplete: (accepted?: boolean) => void;
};

export const AccountAgreement: React.FC<AccountAgreementProps> = ({ securityStamp, reason, onComplete }) => {
    const [agree, setAgreement] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [submiting, setSubmiting] = useState<boolean>(false);
    const [lastResponse, setLastResponse] = useState<StandardAccountAgreeResponse>();
    const [agreementData, setAgreementData] = useState<GetCurrentAgreementQueryResponse | null>(null);
    const [viewError, setViewError] = useState<any>(null);

    const { t } = useTranslation();
    const api = useCoreApi();

    //
    useEffect(() => {
        async function save() {
            try {

                var result = await api.v1.account.standardAccountSetAgreement({
                    securityStamp: securityStamp,
                    agreementId: agreementData!.agreementId,
                    deviceInfo: window.navigator.userAgent,
                    timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
                });

                if (result.data.status == StandardAccountAgreeStatus.Done)
                    onComplete(true);
                else
                    setLastResponse(result.data);
            }
            catch (e: any) {
                setViewError(e);
                setLastResponse(undefined);
            }
            setSubmiting(false);
        }

        if (submiting) save();

    }, [submiting]);

    //
    useEffect(() => {
        async function load() {
            try {
                var result = await api.v1.account.getCurrentAccessAgreement();
                setAgreementData(result.data);
            }
            catch (e: any) {
                setViewError(e);
            }
            setLoading(false);
        }

        if (loading) load();
    }, [loading]);

    if (loading)
        return <MprcRequestIndicator type="request" />;

    //
    return (
        <MprcCard sx={{ minWidth: "70vh", maxWidth: "100vh" }} >
            <MprcCardHeaderColor color="warning" title={t("Auth.AccountAgreement.ViewTitle")} />

            <CardContent>

                {
                    viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : (
                            <Grid container alignItems="center" direction="column" rowSpacing={1}>

                                <Grid width="100%">
                                    {
                                        lastResponse?.status == StandardAccountAgreeStatus.NotFound
                                            ? <Alert severity="warning">{t("Auth.Common.AccountNotFoundMessage")}</Alert>
                                            : lastResponse?.status == StandardAccountAgreeStatus.Expired
                                                ? <Alert severity="warning">{t("Auth.AccountAgreement.ExpiredMessage")}</Alert>
                                                : reason == "Update"
                                                    ? <Alert severity="info">{t("Auth.Common.AgreementUpdatedMessage")}</Alert>
                                                    : reason == "NotAssigned"
                                                        ? <Alert severity="info">{t("Auth.Common.AgreementNotAssignedMessage")}</Alert>
                                                        : null
                                    }
                                </Grid>

                                <Grid width="100%" sx={{ border: "1px solid grey", marginTop: (theme) => theme.spacing(1), padding: (theme) => theme.spacing(2) }}>
                                    <Box
                                        component="div"
                                        sx={{ height: "30vh", width: "100%", overflowY: "auto" }}
                                        dangerouslySetInnerHTML={{ __html: agreementData!.text }}
                                    />
                                </Grid>

                                {
                                    agreementData!.fileUrl &&
                                    <Grid>
                                        <MprcLink href={agreementData!.fileUrl} target="_blank">
                                            <FontAwesomeIcon icon={faDownload} size="2x" />
                                        </MprcLink>
                                    </Grid>
                                }

                                <Grid>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="agreement"
                                                checked={agree}
                                                onChange={
                                                    (e) => {
                                                        setAgreement((e.target as HTMLInputElement).checked);
                                                    }
                                                }
                                                value="agreement"
                                            />
                                        }
                                        label={t("Auth.AccountAgreement.AgreementLabel")}
                                    />
                                </Grid>
                            </Grid>
                        )
                }
            </CardContent>
            <MprcCardActions>

                <ButtonCancel
                    variant="contained"
                    onClick={() => onComplete(undefined)}
                />

                {
                    !viewError &&
                    <ButtonConfirm
                        variant="contained"
                        disabled={!agree}
                        onClick={() => setSubmiting(true)}
                    >
                        {submiting && <MprcSpinnerIndicator />}
                    </ButtonConfirm>
                }
            </MprcCardActions>
        </MprcCard>
    );
};
